import type { ContentRegistry } from '../content/registry';
import type { EraDef, ItemDef, Rarity, StageDef, StatMod, UpgradeDef, WeaponFireDef } from '../content/types';
import { EventQueue } from '../core/events';
import { emptyInput, type InputFrame } from '../core/input';
import { Rng } from '../core/rng';
import type { SimCtx } from './context';
import { anchorStartLevel, rarityWeights, stageScaling, xpToNext } from './formulas';
import { applyMods, BASE_STATS, cloneStats, type Stats } from './stats';
import { updateCollisions } from './systems/collision';
import { coopDensityMult, coopHpMult, firePatternAt, patternDuration, spawnBoss, spawnGroup, spawnEnemy, killEnemy, updateEnemies, updateAttachedPods } from './systems/enemies';
import { collectAll, updatePickups, vacuumAll } from './systems/pickups';
import { updatePlayers } from './systems/player';
import { updateHazards, updateProjectiles } from './systems/projectiles';
import { spawnPets, updatePets } from './systems/pets';
import { PET_BY_ID } from '../content/pets';
import { SIDE_ENEMY, World, wrapDelta } from './world';
import { commitSeconds, isActiveInput, placePortals, PORTAL_BENEFITS, portalDestinations, tallyPortals, type PortalKind, type PortalState } from './portals';
import { characterDef, type CharacterId } from '../content/characters';
import { dcos, dsin } from '../core/dmath';
import type { SkillFlag } from '../content/skills';
import { paradoxDef } from '../content/paradox';

/** Spawn-in hold used by real runs (Game sets `RunConfig.introSeconds`). */
export const INTRO_SECONDS = 2.3;

export type RunPhase = 'playing' | 'levelup' | 'stageVacuum' | 'stageClear' | 'eraClear' | 'dead' | 'victory';

export interface RunConfig {
  seed: number;
  playerCount: number;
  /** Era index to start at (Temporal Anchor). 0 = full run. */
  startEra: number;
  paradox: number;
  /** Permanent stat mods from the hub (shared by all players for now). */
  metaMods: StatMod[];
  /** Equipped items per player; each must include a weapon. */
  equipment: ItemDef[][];
  /** Character identity is simulation input so abilities replay deterministically. */
  characters?: CharacterId[];
  rerolls: number;
  banishes: number;
  /** Upgrade `unlock` tags the player owns. */
  unlocked: Set<string>;
  /** Active pet id per player; null means no companion. */
  pets?: (string | null)[];
  /** Active pet levels, used for deterministic companion scaling. */
  petLevels?: number[];
  /** Temporal Garden tiers; each tier places one run-start heart for that player. */
  startingHeals?: number[];
  /** Named deterministic challenge, when applicable. */
  challengeId?: string;
  /**
   * Seconds at run start before anyone has control (the cosmetic spawn-in
   * entrance plays meanwhile; src/render/entrances.ts). Part of the config so
   * every peer and every replay hands over control on the same tick. 0/omitted
   * = immediate control (tests, tools).
   */
  introSeconds?: number;
  /**
   * Per-player profile overrides (online co-op: every player brings their own
   * meta upgrades, upgrade-pool unlocks and reroll/banish charges). A missing
   * entry falls back to the shared field above, so solo/local configs are unchanged.
   */
  metaModsByPlayer?: StatMod[][];
  /** Validated per-player character keystones. */
  skillFlags?: SkillFlag[][];
  unlockedByPlayer?: Set<string>[];
  rerollsByPlayer?: number[];
  banishesByPlayer?: number[];
}

export interface RunState {
  seed: number;
  phase: RunPhase;
  eraIndex: number;
  stageIndex: number; // 0,1 combat; 2 boss
  globalStage: number;
  stageTime: number;
  stageKills: number;
  /** Generic deterministic progress/state for authored stage objectives. */
  objectiveProgress: number;
  objectiveHealth: number;
  objectiveX: number;
  objectiveY: number;
  elapsed: number;
  level: number;
  xp: number;
  xpToNext: number;
  /**
   * Per-player level-up state. XP and `level` are shared (everyone levels
   * together), but each player owns their picks, offers, charges and banish
   * list — the prerequisite for online co-op (docs/MULTIPLAYER.md).
   * Index by player; use `Sim.offersFor()` / `Sim.upgradesFor()` for reads.
   */
  pendingPicks: number[];
  offers: UpgradeDef[][];
  rerollsLeft: number[];
  banishesLeft: number[];
  banished: Set<string>[];
  upgrades: Map<string, number>[];
  kills: number;
  bossesKilled: number;
  erasCleared: number;
  /** Exact era indices cleared, since route portals can skip forward. */
  clearedEras: number[];
  routeSkips: number;
  noHitStages: number;
  stageDamageTaken: number;
  /** Run-local mastery feats; copied into profile progress only when the run ends. */
  objectiveClears: number;
  perfectDefenses: number;
  fastHunts: number;
  flawlessEscorts: number;
  noHitBosses: number;
  /** Seconds spent visibly pulling the cleared stage's drops into players. */
  stageVacuumTime: number;
  shardsPickedUp: number;
  paradox: number;
  startEra: number;
  // stage director
  spawnAcc: number;
  waveCursor: number;
  bossSpawned: boolean;
  // in-level portals (src/sim/portals.ts) — live during 'stageClear'/'eraClear'
  portals: PortalState[];
  /** Portal index holding a majority, or -1. */
  portalLeader: number;
  /** Seconds until the party travels through `portalLeader`. */
  portalCountdown: number;
  /** Per-player seconds since their last movement/button input (idle players never block). */
  portalIdle: number[];
  /** Phase to return to once pending level-ups are chosen ('playing', or a clear phase). */
  afterPicks: RunPhase;
  /** Bumped on every stage/era advance, so hosts, peers and UI can notice sim-driven travel. */
  advanceSerial: number;
  /** Whether the most recent advance crossed into a new era (drives the time jump). */
  lastAdvanceEra: boolean;
  /** Seconds of spawn-in hold left (see `RunConfig.introSeconds`). */
  introT: number;
}

const RARITIES: Rarity[] = ['common', 'rare', 'epic', 'legendary'];

/**
 * A phase-changing action from a player. Every command goes through
 * `Sim.applyCommand` (optionally tick-stamped via `queueCommand`), so a host
 * can replay a guest's commands at exactly the tick they were issued —
 * see docs/MULTIPLAYER.md.
 */
export type SimCommand =
  | { type: 'chooseUpgrade'; player: number; id: string }
  | { type: 'reroll'; player: number }
  | { type: 'banish'; player: number; id: string }
  | { type: 'advanceStage'; destinationEra?: number };

/**
 * Sim — deterministic game simulation. Owns the World, run state and RNG.
 * Driven by `tick(dt, frames?)` at a fixed rate plus explicit *commands*
 * (chooseUpgrade, reroll, banish, advanceStage). Inputs + commands are the
 * complete input stream; replaying them from the same seed reproduces the run,
 * which is what replays and netcode both ride on.
 */
export class Sim {
  readonly world: World;
  readonly events = new EventQueue();
  readonly rng: Rng; // gameplay
  readonly lootRng: Rng; // upgrade offers (separate so rerolls don't perturb spawns)
  readonly run: RunState;
  readonly ctx: SimCtx;
  readonly config: RunConfig;
  readonly inputs: InputFrame[] = [];
  private era!: EraDef;
  private stage!: StageDef;

  constructor(readonly reg: ContentRegistry, config: RunConfig) {
    this.config = config;
    this.world = new World(config.playerCount);
    this.rng = new Rng(config.seed);
    this.lootRng = new Rng(config.seed ^ 0x5eed1007);
    for (let i = 0; i < config.playerCount; i++) this.inputs.push(emptyInput());

    const durations = new Float32Array(reg.patternList.length);
    reg.patternList.forEach((p, i) => (durations[i] = patternDuration(p)));

    this.ctx = {
      w: this.world,
      reg,
      rng: this.rng,
      events: this.events,
      stats: [],
      weapons: [],
      patternDurations: durations,
      scale: { hp: 1, damage: 1, speed: 1, xp: 1, bossHp: 1, hazardDamage: 1 },
      boss: null,
      playerCount: config.playerCount,
      characters: Array.from({ length: config.playerCount }, (_, i) => config.characters?.[i] ?? 'knight'),
      skillFlags: Array.from({ length: config.playerCount }, (_, i) => new Set(config.skillFlags?.[i] ?? [])),
      godMode: false,
      kills: 0,
      bossKills: 0,
      objectiveKill: null,
      _chainVisited: new Uint8Array(this.world.enemies.pool.cap),
      _np: { idx: -1, dx: 0, dy: 0, d2: 0 },
      _cluster: new Float32Array(2),
      _clusterSet: false,
      _pos: new Float32Array(2),
      _burst: { w: this.world, reg, rng: this.rng, side: SIDE_ENEMY, owner: 0, damage: 1 },
      _weaponEmitter: { shape: 'aimed', interval: 1, bursts: 1, count: 1, bullet: { speed: 1, lifetime: 1, radius: 0.1, damage: 1, visual: 'orb', color: 0xffffff } },
    };

    const startLevel = config.startEra > 0 ? anchorStartLevel(config.startEra) : 1;
    this.run = {
      seed: config.seed,
      phase: 'playing',
      eraIndex: config.startEra,
      stageIndex: 0,
      globalStage: config.startEra * 3,
      stageTime: 0,
      stageKills: 0,
      objectiveProgress: 0,
      objectiveHealth: 0,
      objectiveX: 0,
      objectiveY: 0,
      elapsed: 0,
      level: startLevel,
      xp: 0,
      xpToNext: xpToNext(startLevel),
      pendingPicks: Array.from({ length: config.playerCount }, () => 0),
      offers: Array.from({ length: config.playerCount }, () => [] as UpgradeDef[]),
      rerollsLeft: Array.from({ length: config.playerCount }, (_, i) => config.rerollsByPlayer?.[i] ?? config.rerolls),
      banishesLeft: Array.from({ length: config.playerCount }, (_, i) => config.banishesByPlayer?.[i] ?? config.banishes),
      banished: Array.from({ length: config.playerCount }, () => new Set<string>()),
      upgrades: Array.from({ length: config.playerCount }, () => new Map<string, number>()),
      kills: 0,
      bossesKilled: 0,
      erasCleared: 0,
      clearedEras: [],
      routeSkips: 0,
      noHitStages: 0,
      stageDamageTaken: 0,
      objectiveClears: 0,
      perfectDefenses: 0,
      fastHunts: 0,
      flawlessEscorts: 0,
      noHitBosses: 0,
      stageVacuumTime: 0,
      shardsPickedUp: 0,
      paradox: config.paradox,
      startEra: config.startEra,
      spawnAcc: 0,
      waveCursor: 0,
      bossSpawned: false,
      portals: [],
      portalLeader: -1,
      portalCountdown: 0,
      portalIdle: Array.from({ length: config.playerCount }, () => 0),
      afterPicks: 'playing',
      advanceSerial: 0,
      lastAdvanceEra: false,
      introT: Math.max(0, config.introSeconds ?? 0),
    };
    this.ctx.objectiveKill = (enemyId) => {
      const objective = this.stage?.objective;
      if (objective?.type === 'hunt' && objective.enemy === enemyId) this.run.objectiveProgress++;
    };
    this.recomputeStats();
    for (const p of this.world.players) p.hp = this.ctx.stats[p.index].maxHp;
    this.loadStage();
    // Anchored starts: the level number alone is worthless — grant the upgrade picks that come with it.
    if (startLevel > 1) {
      this.run.pendingPicks = this.run.pendingPicks.map(() => startLevel - 1);
      this.openLevelUp();
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Stats                                                                   */
  /* ---------------------------------------------------------------------- */

  /** Recompute per-player stats from meta → character passive → equipment → run upgrades. */
  recomputeStats(): void {
    for (let i = 0; i < this.config.playerCount; i++) {
      // per-player upgrade pool (co-op: everyone builds their own character)
      const runMods: StatMod[] = [];
      for (const [id, stacks] of this.run.upgrades[i] ?? []) {
        const u = this.reg.upgrades.get(id);
        if (!u) continue;
        for (let s = 0; s < stacks; s++) runMods.push(...u.mods);
      }
      const equip = this.config.equipment[i] ?? [];
      const equipMods: StatMod[] = [];
      let weapon: WeaponFireDef | undefined;
      for (const it of equip) {
        equipMods.push(...it.mods);
        if (it.weapon) {
          weapon = it.weapon;
          if (it.weapon.ruleMods) equipMods.push(...it.weapon.ruleMods);
        }
      }
      if (!weapon) weapon = this.reg.itemList.find((i) => i.slot === 'weapon')?.weapon;
      if (!weapon) throw new Error('No weapon available in content');
      const prev = this.ctx.stats[i];
      const passiveMods = characterDef(this.ctx.characters[i]).passive.mods;
      const petMods = PET_BY_ID.get(this.config.pets?.[i] ?? '')?.passive.mods ?? [];
      const stats = applyMods(BASE_STATS, [this.config.metaModsByPlayer?.[i] ?? this.config.metaMods, passiveMods, petMods, equipMods, runMods], prev ?? cloneStats(BASE_STATS));
      this.ctx.stats[i] = stats;
      this.ctx.weapons[i] = weapon;
      const p = this.world.players[i];
      const maxAbilityCharges = this.ctx.characters[i] === 'knight' ? stats.dashCharges : 1;
      if (p.dashChargesLeft > maxAbilityCharges) p.dashChargesLeft = maxAbilityCharges;
      if (p.hp > stats.maxHp) p.hp = stats.maxHp;
    }
  }

  getStats(player = 0): Stats {
    return this.ctx.stats[player];
  }

  /* ---------------------------------------------------------------------- */
  /* Stage director                                                          */
  /* ---------------------------------------------------------------------- */

  get currentEra(): EraDef {
    return this.era;
  }
  get currentStage(): StageDef {
    return this.stage;
  }

  /** Point era/stage refs at the given indices WITHOUT resetting entities (used by snapshot restore). */
  reloadStage(eraIndex: number, stageIndex: number): void {
    this.era = this.reg.eras[eraIndex];
    this.stage = this.era.stages[stageIndex];
    const sc = stageScaling(this.run.globalStage, this.run.paradox);
    this.ctx.scale.hp = sc.hp * coopHpMult(this.config.playerCount);
    this.ctx.scale.damage = sc.damage;
    this.ctx.scale.speed = sc.speed;
    this.ctx.scale.xp = sc.xp;
    this.ctx.scale.bossHp = sc.bossHp;
    this.ctx.scale.hazardDamage = sc.hazardDamage;
  }

  private loadStage(): void {
    const r = this.run;
    this.era = this.reg.eras[r.eraIndex];
    this.stage = this.era.stages[r.stageIndex];
    this.world.arenaRadius = this.era.environment.arenaRadius;
    this.world.grid.resize(this.world.arenaRadius);
    this.world.clearEntities();
    this.ctx.boss = null;
    r.stageTime = 0;
    r.stageKills = 0;
    r.objectiveProgress = 0;
    r.objectiveHealth = this.stage.objective?.type === 'defend' || this.stage.objective?.type === 'escort' ? this.stage.objective.health ?? 100 : 0;
    r.objectiveX = 0;
    r.objectiveY = 0;
    r.stageDamageTaken = 0;
    r.stageVacuumTime = 0;
    r.spawnAcc = 0;
    r.waveCursor = 0;
    r.bossSpawned = false;
    const sc = stageScaling(r.globalStage, r.paradox);
    const coop = coopHpMult(this.config.playerCount);
    this.ctx.scale.hp = sc.hp * coop;
    this.ctx.scale.damage = sc.damage;
    this.ctx.scale.speed = sc.speed;
    this.ctx.scale.xp = sc.xp;
    this.ctx.scale.bossHp = sc.bossHp;
    this.ctx.scale.hazardDamage = sc.hazardDamage;
    // reposition players in a small ring at the centre
    const n = this.world.players.length;
    this.world.players.forEach((p, i) => {
      const a = (i / n) * Math.PI * 2;
      p.x = n > 1 ? dcos(a) * 1.5 : 0;
      p.y = n > 1 ? dsin(a) * 1.5 : 0;
      p.px = p.x;
      p.py = p.y;
      p.vx = 0;
      p.vy = 0;
      p.iframes = 1;
      p.stillTime = 0;
      p.targetEnemy = 0;
      if (!p.alive) {
        p.alive = true; // co-op: revive dead players at stage start
        p.hp = this.ctx.stats[i].maxHp * 0.5;
      }
    });
    if (r.globalStage === this.config.startEra * 3) {
      const pickups = this.world.pickups;
      for (const p of this.world.players) {
        const count = Math.max(0, Math.min(3, this.config.startingHeals?.[p.index] ?? 0));
        for (let n = 0; n < count; n++) {
          const slot = pickups.pool.alloc();
          if (!slot) break;
          const a = (n + 1) * 1.7 + p.index;
          pickups.x[slot] = p.x + dcos(a) * (2.2 + n * 0.35);
          pickups.y[slot] = p.y + dsin(a) * (2.2 + n * 0.35);
          pickups.kind[slot] = 1;
          pickups.amount[slot] = 0.15;
          pickups.life[slot] = 45;
          pickups.magnet[slot] = -1;
        }
      }
    }
    spawnPets(this.ctx, this.config.pets ?? [], this.config.petLevels ?? []);
    r.phase = 'playing';
  }

  private directStage(dt: number): void {
    const r = this.run;
    const st = this.stage;
    r.stageTime += dt;
    const t = r.stageTime;
    const boss = st.boss;

    // scripted waves
    while (r.waveCursor < st.waves.length && st.waves[r.waveCursor].t <= t) {
      const wv = st.waves[r.waveCursor++];
      if (!boss || !r.bossSpawned) {
        spawnGroup(this.ctx, wv.enemy, wv.count, wv.formation ?? 'edge', wv.elite, st.objective?.type === 'waves');
      }
    }

    // boss
    if (boss && !r.bossSpawned && t >= boss.at) {
      r.bossSpawned = true;
      spawnBoss(this.ctx, boss.id);
    }

    // background density (stops when boss is out)
    const wavesCleared = st.objective?.type === 'waves' && r.waveCursor >= st.waves.length;
    if (!(boss && r.bossSpawned) && !wavesCleared) {
      const f = Math.min(1, t / st.duration);
      const mutators = paradoxDef(r.paradox);
      const rate = (st.density.start + (st.density.end - st.density.start) * f) * coopDensityMult(this.config.playerCount) * mutators.density;
      r.spawnAcc += rate * dt;
      const cap = 320;
      while (r.spawnAcc >= 1) {
        r.spawnAcc -= 1;
        if (this.world.enemies.pool.count >= cap) continue;
        const pool = st.pool.filter((p) => (p.minTime ?? 0) <= t);
        if (pool.length === 0) break;
        const idx = this.rng.weightedIndex(pool.map((p) => {
          const archetype = this.reg.enemies.get(p.enemy)?.archetype;
          return p.weight * (archetype ? mutators.spawnWeights[archetype] ?? 1 : 1);
        }));
        const elite = this.rng.chance(0.015 + mutators.eliteChanceAdd);
        spawnGroup(this.ctx, pool[idx].enemy, 1, 'edge', elite);
      }
    }

    // completion
    if (boss) {
      if (r.bossSpawned && this.ctx.boss === null) this.completeStage();
    } else if (this.objectiveComplete(st)) {
      this.completeStage();
    }
  }

  private objectiveComplete(st: StageDef): boolean {
    const objective = st.objective;
    if (!objective) return this.run.stageTime >= st.duration || (st.killTarget !== undefined && this.run.stageKills >= st.killTarget);
    if (objective.type === 'survive') return this.run.stageTime >= (objective.seconds ?? st.duration);
    if (objective.type === 'kill') return this.run.stageKills >= objective.count;
    if (objective.type === 'waves') {
      if (this.run.waveCursor < st.waves.length) return false;
      const enemies = this.world.enemies;
      for (let i = 1; i < enemies.pool.high; i++) {
        if (enemies.pool.alive[i] && enemies.objectiveWave[i] === 1) return false;
      }
      return true;
    }
    if (objective.type === 'hold' || objective.type === 'collect' || objective.type === 'hunt') return this.run.objectiveProgress >= (objective.type === 'hold' ? objective.seconds : objective.count);
    if (objective.type === 'defend') return this.run.stageTime >= objective.seconds && this.run.objectiveHealth > 0;
    return this.run.objectiveProgress >= objective.distance && this.run.objectiveHealth > 0;
  }

  /** Deterministic objective mechanics; all state lives in RunState for snapshots/replays. */
  private updateObjective(dt: number): void {
    const objective = this.stage.objective;
    if (!objective || (objective.type !== 'hold' && objective.type !== 'defend' && objective.type !== 'escort')) return;
    const r = this.run;
    const radius = objective.radius ?? (objective.type === 'escort' ? 5 : 6);
    const nearPlayer = this.world.players.some((p) => {
      if (!p.alive) return false;
      const dx = wrapDelta(p.x - r.objectiveX, this.world.half);
      const dy = wrapDelta(p.y - r.objectiveY, this.world.half);
      return dx * dx + dy * dy <= radius * radius;
    });
    if (objective.type === 'hold') {
      if (nearPlayer) r.objectiveProgress += dt;
      return;
    }
    let attackers = 0;
    const e = this.world.enemies;
    for (let i = 1; i < e.pool.high; i++) {
      if (!e.pool.alive[i]) continue;
      const dx = wrapDelta(e.x[i] - r.objectiveX, this.world.half);
      const dy = wrapDelta(e.y[i] - r.objectiveY, this.world.half);
      if (dx * dx + dy * dy <= 2.6 * 2.6) attackers++;
    }
    r.objectiveHealth = Math.max(0, r.objectiveHealth - attackers * 4 * dt);
    if (objective.type === 'escort' && nearPlayer && r.objectiveHealth > 0) {
      const step = Math.min(objective.distance - r.objectiveProgress, (objective.speed ?? 1.7) * dt);
      r.objectiveProgress += step;
      r.objectiveX = r.objectiveProgress;
    }
    if (r.objectiveHealth <= 0) {
      r.phase = 'dead';
      this.events.push({ type: 'runOver', victory: false });
    }
  }

  private completeStage(): void {
    const r = this.run;
    r.introT = 0;
    if (r.stageDamageTaken <= 0) r.noHitStages++;
    const objective = this.stage.objective;
    if (r.stageIndex === 2) {
      if (r.stageDamageTaken <= 0) r.noHitBosses++;
    } else if (objective) {
      r.objectiveClears++;
      const fullHealth = objective.type === 'defend' || objective.type === 'escort' ? objective.health ?? 100 : 0;
      if (objective.type === 'defend' && r.objectiveHealth >= fullHealth) r.perfectDefenses++;
      if (objective.type === 'escort' && r.objectiveHealth >= fullHealth) r.flawlessEscorts++;
      if (objective.type === 'hunt' && r.stageTime <= 120) r.fastHunts++;
    }
    // Clear combat immediately, then leave pickups visible long enough to fly
    // into the players before showing the stage-clear overlay.
    const e = this.world.enemies;
    for (let i = 1; i < e.pool.high; i++) if (e.pool.alive[i]) e.pool.release(i);
    this.world.projectiles.pool.reset();
    this.world.hazards.pool.reset();
    r.phase = 'stageVacuum';
    r.stageVacuumTime = 0;
    vacuumAll(this.ctx);
    if (this.world.pickups.pool.count === 0) this.finishStageClear();
  }

  /** Apply stage rewards after the visible pickup vacuum has completed. */
  private finishStageClear(): void {
    const r = this.run;
    // A timeout fallback below can land here with a pathological pickup still
    // alive; credit it exactly once rather than letting stage loading erase it.
    collectAll(this.ctx, this.onCollect);
    r.phase = 'stageClear';
    this.addXp(40 * (1 + r.globalStage * 0.25));
    this.events.push({ type: 'stageComplete', stage: r.stageIndex });
    if (r.stageIndex === 2) {
      r.erasCleared++;
      if (!r.clearedEras.includes(r.eraIndex)) r.clearedEras.push(r.eraIndex);
      this.addXp(80 * (1 + r.eraIndex * 0.5));
      this.events.push({ type: 'eraComplete', eraIndex: r.eraIndex });
      const isLast = r.eraIndex >= this.reg.eras.length - 1 || !this.reg.eras[r.eraIndex + 1]?.implemented;
      r.phase = isLast ? 'victory' : 'eraClear';
      if (isLast) this.events.push({ type: 'runOver', victory: true });
    } else {
      r.phase = 'stageClear';
    }
    this.resetPortals();
    // Level-ups earned by the clear resolve first; the portals open afterwards.
    if (r.phase !== 'victory' && r.pendingPicks.some((n) => n > 0)) {
      r.afterPicks = r.phase;
      this.openLevelUp();
    }
  }

  /** Stage/era cleared: the portals phase (or about to be, once picks resolve). */
  isClearPhase(phase: RunPhase): boolean {
    return phase === 'stageClear' || phase === 'eraClear';
  }

  private resetPortals(): void {
    const r = this.run;
    r.portals = [];
    r.portalLeader = -1;
    r.portalCountdown = 0;
    r.portalIdle = r.portalIdle.map(() => 0);
  }

  /** Portal phase step: players roam, portals open, and a majority commits travel. */
  private tickPortals(dt: number): void {
    const r = this.run;
    const w = this.world;
    w.tick++;
    w.time += dt;
    r.elapsed += dt;
    updatePlayers(this.ctx, this.inputs, dt);
    updatePets(this.ctx, dt);
    updateProjectiles(w, this.reg, this.rng, dt, this.onExpirePattern);
    updatePickups(this.ctx, dt, this.onCollect);
    for (let i = 0; i < r.portalIdle.length; i++) {
      const f = this.inputs[i];
      r.portalIdle[i] = f && isActiveInput(f) ? 0 : r.portalIdle[i] + dt;
    }
    if (r.portals.length === 0) {
      const anchor = w.players.find((p) => p.alive) ?? w.players[0];
      r.portals = placePortals(portalDestinations(this.reg, r.eraIndex, r.phase === 'eraClear'), anchor.x, anchor.y, w.half);
      this.events.push({ type: 'portalsOpen' });
    }
    const tally = tallyPortals(r.portals, w.players, r.portalIdle, w.half);
    if (tally.leader < 0) {
      r.portalLeader = -1;
      r.portalCountdown = 0;
      return;
    }
    if (tally.leader !== r.portalLeader) {
      r.portalLeader = tally.leader;
      r.portalCountdown = commitSeconds(this.config.playerCount);
      return;
    }
    r.portalCountdown -= dt;
    if (r.portalCountdown <= 0) {
      const portal = r.portals[tally.leader];
      if (this.advanceStage(portal.kind === 'stage' ? undefined : portal.era)) this.applyPortalBenefit(portal.kind);
    }
  }

  private applyPortalBenefit(kind: PortalKind): void {
    const benefit = PORTAL_BENEFITS[kind];
    const r = this.run;
    for (const player of this.world.players) {
      if (!player.alive) continue;
      const maxHp = this.ctx.stats[player.index].maxHp;
      player.hp = Math.min(maxHp, player.hp + maxHp * benefit.healFraction);
      r.pendingPicks[player.index] += benefit.bonusPicks;
      r.rerollsLeft[player.index] += benefit.bonusRerolls;
    }
    r.shardsPickedUp += benefit.bonusShards;
    this.events.push({ type: 'portalBenefit', title: benefit.title });
    if (benefit.bonusPicks > 0 && r.phase === 'playing') this.openLevelUp();
  }

  /** Leave the level-up pause for wherever the picks interrupted. */
  private endPicks(): void {
    const r = this.run;
    r.phase = r.afterPicks;
    r.afterPicks = 'playing';
  }

  /** Command: proceed to the next stage/era after a stageClear/eraClear phase. */
  advanceStage(destinationEra?: number): boolean {
    const r = this.run;
    // Also legal while a clear's level-ups are open: travel carries the picks into the next stage.
    if (!this.isClearPhase(r.phase) && !(r.phase === 'levelup' && this.isClearPhase(r.afterPicks))) return false;
    const crossingEra = r.stageIndex === 2;
    if (r.stageIndex === 2) {
      const next = r.eraIndex + 1;
      const destination = destinationEra ?? next;
      if (destination < next || destination >= this.reg.eras.length || !this.reg.eras[destination]?.implemented) return false;
      r.stageIndex = 0;
      r.routeSkips += Math.max(0, destination - next);
      r.eraIndex = destination;
      r.globalStage = destination * 3;
    } else {
      r.stageIndex++;
      r.globalStage++;
    }
    this.resetPortals();
    r.afterPicks = 'playing';
    r.advanceSerial++;
    r.lastAdvanceEra = crossingEra;
    this.events.push({ type: 'stageAdvanced', era: crossingEra });
    this.loadStage();
    if (r.pendingPicks.some((n) => n > 0)) this.openLevelUp();
    return true;
  }

  /* ---------------------------------------------------------------------- */
  /* XP & upgrades                                                           */
  /* ---------------------------------------------------------------------- */

  addXp(amount: number, player = 0): void {
    const r = this.run;
    r.xp += amount * this.ctx.stats[player].xpMult;
    while (r.xp >= r.xpToNext) {
      r.xp -= r.xpToNext;
      r.level++;
      r.xpToNext = xpToNext(r.level);
      // shared XP, individual picks: every living player earns a pick
      for (const pl of this.world.players) if (pl.alive) r.pendingPicks[pl.index]++;
      this.events.push({ type: 'levelUp', player, level: r.level });
    }
    if (r.pendingPicks.some((n) => n > 0) && r.phase === 'playing') this.openLevelUp();
  }

  /**
   * The first player who still owes a pick, or -1 when nobody does. Every
   * owing player has offers at once (picks are simultaneous, v0.23.0); local
   * co-op's single screen still walks through them in this order.
   */
  pickingPlayer(): number {
    return this.run.pendingPicks.findIndex((n) => n > 0);
  }

  /** Every player who still owes a pick, in index order. */
  pickingPlayers(): number[] {
    const out: number[] = [];
    this.run.pendingPicks.forEach((n, i) => {
      if (n > 0) out.push(i);
    });
    return out;
  }

  /** The upgrades a player has taken this run. */
  upgradesFor(player = 0): Map<string, number> {
    return this.run.upgrades[player] ?? new Map();
  }

  /** The offers currently in front of a player. */
  offersFor(player = 0): UpgradeDef[] {
    return this.run.offers[player] ?? [];
  }

  /**
   * Enter the level-up pause and roll offers for every player who owes a pick
   * and has none showing yet (index order, so `lootRng` use is deterministic).
   */
  private openLevelUp(): void {
    if (this.pickingPlayer() < 0) return;
    this.run.phase = 'levelup';
    for (const p of this.pickingPlayers()) {
      if (this.run.offers[p].length === 0 && this.run.pendingPicks[p] > 0) this.run.offers[p] = this.rollOffers(p);
    }
    if (this.run.pendingPicks.every((n) => n <= 0)) this.endPicks();
  }

  /** Weighted-random 3 upgrade offers (rarity weights shifted by luck; evolutions guaranteed when available). */
  rollOffers(player = 0, count = 3): UpgradeDef[] {
    const r = this.run;
    const luck = this.ctx.stats[player].luck;
    const weights = rarityWeights(luck);
    const pool = r.upgrades[player];
    const owned = (id: string) => pool.get(id) ?? 0;
    const eligible = this.reg.upgradeList.filter((u) => {
      if (r.banished[player].has(u.id)) return false;
      if (owned(u.id) >= u.maxStacks) return false;
      if (u.unlock && !(this.config.unlockedByPlayer?.[player] ?? this.config.unlocked).has(u.unlock)) return false;
      if (u.characters && !u.characters.includes(this.ctx.characters[player])) return false;
      if (u.requires && !u.requires.every((req) => owned(req) > 0)) return false;
      return true;
    });
    const offers: UpgradeDef[] = [];
    // evolutions first
    for (const u of eligible) if (u.evolution && offers.length < count) offers.push(u);
    let guard = 0;
    while (offers.length < count && guard++ < 200) {
      const rarity = RARITIES[this.lootRng.weightedIndex(weights)];
      const pool = eligible.filter((u) => u.rarity === rarity && !u.evolution && !offers.includes(u));
      if (pool.length === 0) continue;
      const idx = this.lootRng.weightedIndex(pool.map((u) => u.weight ?? 1));
      offers.push(pool[idx]);
    }
    if (offers.length === 0) {
      // nothing left to offer: grant a small heal instead
      const pl = this.world.players[player];
      if (pl) pl.hp = Math.min(this.ctx.stats[player].maxHp, pl.hp + 20);
      r.pendingPicks[player] = 0;
      if (r.pendingPicks.every((n) => n <= 0)) this.endPicks();
    }
    return offers;
  }

  /** Command: pick an offered upgrade (defaults to player 0 — single-player callers need no change). */
  chooseUpgrade(id: string, player = 0): boolean {
    const r = this.run;
    if (r.phase !== 'levelup') return false;
    if (r.pendingPicks[player] <= 0) return false;
    const u = r.offers[player]?.find((o) => o.id === id);
    if (!u) return false;
    this.applyUpgrade(u, player);
    r.pendingPicks[player]--;
    r.offers[player] = [];
    // Picks are simultaneous: only this player's next offer is rolled; others keep theirs.
    if (r.pendingPicks[player] > 0) r.offers[player] = this.rollOffers(player);
    if (r.pendingPicks.every((n) => n <= 0)) this.endPicks();
    return true;
  }

  applyUpgrade(u: UpgradeDef, player = 0): void {
    const r = this.run;
    const pool = r.upgrades[player];
    pool.set(u.id, (pool.get(u.id) ?? 0) + 1);
    if (u.evolution && u.requires) for (const req of u.requires) pool.set(req, u.maxStacks); // lock ingredients
    const before = this.ctx.stats.map((s) => s.maxHp);
    this.recomputeStats();
    // maxHp increases heal by the difference (only for the player who took it)
    this.world.players.forEach((p, i) => {
      const d = this.ctx.stats[i].maxHp - before[i];
      if (d > 0) p.hp = Math.min(this.ctx.stats[i].maxHp, p.hp + d);
    });
    if (u.category === 'health' && u.tags?.includes('heal')) {
      const p = this.world.players[player];
      if (p) p.hp = this.ctx.stats[player].maxHp;
    }
  }

  /** Command: reroll a player's offers (costs one of their charges). */
  reroll(player = 0): boolean {
    const r = this.run;
    if (r.phase !== 'levelup' || r.pendingPicks[player] <= 0 || r.rerollsLeft[player] <= 0) return false;
    r.rerollsLeft[player]--;
    r.offers[player] = this.rollOffers(player);
    return true;
  }

  /** Command: banish an offered upgrade for the rest of the run (costs one of that player's charges). */
  banish(id: string, player = 0): boolean {
    const r = this.run;
    if (r.phase !== 'levelup' || r.pendingPicks[player] <= 0 || r.banishesLeft[player] <= 0) return false;
    if (!r.offers[player].some((o) => o.id === id)) return false;
    r.banishesLeft[player]--;
    r.banished[player].add(id);
    r.offers[player] = this.rollOffers(player);
    return true;
  }

  /* ---------------------------------------------------------------------- */
  /* Tick                                                                    */
  /* ---------------------------------------------------------------------- */

  private onExpirePattern = (patternIdx: number, x: number, y: number, side: number) => firePatternAt(this.ctx, patternIdx, x, y, side);

  private onCollect = (player: number, kind: number, amount: number) => {
    const p = this.world.players[player];
    const s = this.ctx.stats[player];
    if (kind === 0) {
      this.addXp(amount, player); // shared XP: one pool for the party
      if (this.stage.objective?.type === 'collect') this.run.objectiveProgress++;
      this.events.push({ type: 'pickup', player, kind: 'xp', amount });
    } else if (kind === 1) {
      const healed = Math.min(s.maxHp - p.hp, s.maxHp * amount);
      p.hp += healed;
      this.events.push({ type: 'pickup', player, kind: 'heal', amount: healed });
    } else {
      const n = Math.round(amount * s.shardMult);
      this.run.shardsPickedUp += n;
      this.events.push({ type: 'pickup', player, kind: 'shard', amount: n });
    }
  };

  /** Advance one fixed step. Only runs in the `playing` phase; other phases are paused by design. */
  /**
   * Apply a command now. Single entry point for every phase-changing action,
   * so a host can route a guest's commands through one place (and a replay can
   * record them). Returns false if the command was not legal in this state.
   */
  applyCommand(cmd: SimCommand): boolean {
    switch (cmd.type) {
      case 'chooseUpgrade':
        return this.chooseUpgrade(cmd.id, cmd.player);
      case 'reroll':
        return this.reroll(cmd.player);
      case 'banish':
        return this.banish(cmd.id, cmd.player);
      case 'advanceStage':
        return this.advanceStage(cmd.destinationEra);
    }
  }

  /**
   * Queue a command to be applied at the start of tick `atTick` (or the next
   * tick, if that one has passed). Two sims fed the same inputs and the same
   * queued commands stay bit-identical — see `hashState()`.
   */
  queueCommand(cmd: SimCommand, atTick: number): void {
    this.commandQueue.push({ tick: atTick, cmd });
    this.commandQueue.sort((a, b) => a.tick - b.tick);
  }

  private commandQueue: { tick: number; cmd: SimCommand }[] = [];

  private drainCommands(): void {
    const now = this.world.tick;
    while (this.commandQueue.length > 0 && this.commandQueue[0].tick <= now) {
      this.applyCommand(this.commandQueue.shift()!.cmd);
    }
  }

  /**
   * Advance one fixed step. `frames` (one per player, in player order) replaces
   * the local `inputs` array for this tick — that is the shape a host receives
   * from the network; local play keeps mutating `sim.inputs` and passes nothing.
   */
  tick(dt: number, frames?: readonly InputFrame[]): void {
    if (frames) {
      for (let i = 0; i < this.inputs.length && i < frames.length; i++) {
        const src = frames[i];
        const dst = this.inputs[i];
        dst.mx = src.mx;
        dst.my = src.my;
        dst.dash = src.dash;
        dst.action = src.action;
      }
    }
    this.drainCommands();
    const r = this.run;
    if (r.phase === 'stageVacuum') {
      const w = this.world;
      w.tick++;
      w.time += dt;
      r.elapsed += dt;
      r.stageVacuumTime += dt;
      // Faster than the ordinary pickup magnet so even edge-of-arena gems
      // complete their visible flight without making the player wait.
      updatePickups(this.ctx, dt, this.onCollect, 2.75);
      if (w.pickups.pool.count === 0 || r.stageVacuumTime >= 2.25) this.finishStageClear();
      return;
    }
    if (r.phase === 'stageClear' || r.phase === 'eraClear') {
      this.tickPortals(dt);
      return;
    }
    if (r.phase !== 'playing') return;
    const w = this.world;
    if (r.introT > 0) {
      // Spawn-in hold: the world is frozen and nobody has control until it ends.
      w.tick++;
      w.time += dt;
      r.introT = Math.max(0, r.introT - dt);
      for (const p of w.players) {
        p.px = p.x;
        p.py = p.y;
      }
      return;
    }
    w.tick++;
    w.time += dt;
    r.elapsed += dt;

    const takenBefore = w.players.reduce((a, p) => a + p.damageTaken, 0);

    updatePlayers(this.ctx, this.inputs, dt);
    updatePets(this.ctx, dt);
    w.grid.build(w.enemies); // fresh grid for enemy separation (rebuilt again after movement, in collisions)
    updateEnemies(this.ctx, dt);
    updateAttachedPods(this.ctx, dt); // glue subsystems pods to their boss's rotating rack post-movement
    updateProjectiles(w, this.reg, this.rng, dt, this.onExpirePattern);
    updateHazards(w, dt);
    updateCollisions(this.ctx, dt);
    updatePickups(this.ctx, dt, this.onCollect);

    r.stageKills += this.ctx.kills - r.kills;
    r.kills = this.ctx.kills;
    r.bossesKilled = this.ctx.bossKills;
    const taken = w.players.reduce((a, p) => a + p.damageTaken, 0);
    r.stageDamageTaken += taken - takenBefore;

    if (r.phase === 'playing') this.updateObjective(dt);
    if (r.phase === 'playing') this.directStage(dt);

    if (r.phase === 'playing' && w.players.every((p) => !p.alive)) {
      r.phase = 'dead';
      this.events.push({ type: 'runOver', victory: false });
    }
  }

  /**
   * Cheap FNV-1a hash of everything the simulation owns, quantized so float
   * noise can't flap it. Two sims that have seen the same inputs and commands
   * must return the same value every tick — the desync check for netcode
   * (docs/MULTIPLAYER.md) and the sharpest test of determinism we have.
   */
  hashState(): number {
    let h = 0x811c9dc5;
    const mix = (v: number) => {
      h ^= v | 0;
      h = Math.imul(h, 0x01000193) >>> 0;
    };
    const q = (v: number) => Math.round(v * 1000);
    const w = this.world;
    mix(w.tick);
    for (const v of this.rng.getState()) mix(v);
    for (const v of this.lootRng.getState()) mix(v);
    for (const character of this.ctx.characters) mix(character === 'knight' ? 1 : character === 'barbarian' ? 2 : character === 'mage' ? 3 : character === 'ranger' ? 4 : 5);
    mix(this.run.level);
    mix(q(this.run.xp));
    mix(this.ctx.kills);
    for (const p of w.players) {
      mix(q(p.x));
      mix(q(p.y));
      mix(q(p.hp));
      mix(p.alive ? 1 : 0);
      mix(q(p.moveDx));
      mix(q(p.moveDy));
      mix(q(p.dashT));
      mix(q(p.dashBufferT));
      mix(q(p.dashCdT));
      mix(p.dashChargesLeft);
      mix(p.keystoneShield ? 1 : 0);
      mix(q(p.keystoneCooldown));
      mix(p.keystoneCounter);
      mix(q(p.keystoneWindow));
      mix(q(p.deadeyeCharge));
      mix(p.deadeyeArmed ? 1 : 0);
    }
    const r = this.run;
    mix(q(r.introT));
    mix(q(r.objectiveProgress));
    mix(q(r.objectiveHealth));
    mix(q(r.objectiveX));
    mix(q(r.objectiveY));
    mix(r.objectiveClears);
    mix(r.perfectDefenses);
    mix(r.fastHunts);
    mix(r.flawlessEscorts);
    mix(r.noHitBosses);
    mix(r.advanceSerial);
    mix(r.shardsPickedUp);
    for (const n of r.pendingPicks) mix(n);
    for (const n of r.rerollsLeft) mix(n);
    mix(r.portalLeader);
    mix(q(r.portalCountdown));
    for (const portal of r.portals) {
      mix(portal.era);
      mix(q(portal.x));
      mix(q(portal.y));
    }
    const e = w.enemies;
    for (let i = 1; i < e.pool.high; i++) {
      if (!e.pool.alive[i]) continue;
      mix(i);
      mix(q(e.x[i]));
      mix(q(e.y[i]));
      mix(q(e.hp[i]));
      mix(e.objectiveWave[i]);
    }
    const pr = w.projectiles;
    for (let i = 1; i < pr.pool.high; i++) {
      if (!pr.pool.alive[i]) continue;
      mix(i);
      mix(q(pr.x[i]));
      mix(q(pr.y[i]));
    }
    const pets = w.pets;
    for (let i = 1; i < pets.pool.high; i++) {
      if (!pets.pool.alive[i]) continue;
      mix(i);
      mix(pets.owner[i]);
      mix(pets.def[i]);
      mix(q(pets.x[i]));
      mix(q(pets.y[i]));
      mix(q(pets.fireT[i]));
    }
    return h >>> 0;
  }

  /* ---------------------------------------------------------------------- */
  /* Debug commands                                                          */
  /* ---------------------------------------------------------------------- */

  debugSpawn(enemyId: string, count = 1, elite = false): void {
    spawnGroup(this.ctx, enemyId, count, 'ring', elite);
  }
  debugSpawnAt(enemyId: string, x: number, y: number): number {
    return spawnEnemy(this.ctx, enemyId, x, y);
  }
  debugKillAll(): void {
    const e = this.world.enemies;
    for (let i = 1; i < e.pool.high; i++) if (e.pool.alive[i]) killEnemy(this.ctx, i, 0);
  }
  debugSkipStage(): void {
    if (this.run.phase === 'playing') this.completeStage();
  }
  debugLevelUp(): void {
    this.addXp(this.run.xpToNext - this.run.xp + 1);
  }
  setGodMode(on: boolean): void {
    this.ctx.godMode = on;
  }
}
